import { Timestamp } from '@griply/types';
import { isBefore, isSameDay, startOfDay } from 'date-fns';

import { interpolateDataEntries } from './interpolate-data-entries';

export type TimeProgressEntry = {
  date: Timestamp;
  value: number;
};

export type PrepareTimeProgressEntriesOptions = {
  startDate?: Timestamp | null;
  startValue?: number | null;
  today: Timestamp;
  completed?: boolean;
  chartStartDate?: Timestamp | null;
  chartEndDate?: Timestamp | null;
};

export const prepareTimeProgressEntries = (
  data: TimeProgressEntry[],
  {
    startDate,
    startValue,
    today,
    completed,
    chartStartDate,
    chartEndDate,
  }: PrepareTimeProgressEntriesOptions,
): TimeProgressEntry[] => {
  const entries = data.map(({ date, value }) => ({
    date: startOfDay(date),
    value,
  }));

  if (completed) {
    return chartStartDate && chartEndDate
      ? interpolateDataEntries(entries, chartStartDate, chartEndDate)
      : entries;
  }

  if (startDate) {
    const startDateEntry = data.find(({ date }) => isSameDay(date, startDate));
    const beforeStartDateEntry = data.find(({ date }) =>
      isBefore(date, startDate),
    );

    if (!startDateEntry && !beforeStartDateEntry) {
      entries.push({ date: startDate, value: startValue ?? 0 });
    }
  }

  const todayEntry = data.find(({ date }) => isSameDay(date, today));
  if (!todayEntry) {
    const sortedData = [...data].sort(
      (a, b) => a.date.getTime() - b.date.getTime(),
    );
    const lastValue =
      sortedData[sortedData.length - 1]?.value ?? startValue ?? 0;
    entries.push({ date: today, value: lastValue });
  }

  entries.sort((a, b) => a.date.getTime() - b.date.getTime());

  return chartStartDate && chartEndDate
    ? interpolateDataEntries(entries, chartStartDate, chartEndDate)
    : entries;
};
